import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, ChefHat, Clock, Users, ArrowRight } from 'lucide-react'
import { PageSpinner } from '@/components/ui/Spinner'
import { useMenuCategories, useMenuItems } from '@/hooks/useMenu'

// Read-only index over every product that has an AI-extracted (or manually
// entered) recipe on file — "View Recipe" deep-links straight into that
// product's Recipe & SOP tab rather than duplicating the print/preview UI here.
export function RecipesPage() {
  const { data: allItems, isLoading: itemsLoading } = useMenuItems()
  const { data: categories, isLoading: catsLoading } = useMenuCategories()
  const [search, setSearch] = useState('')

  const catMap = Object.fromEntries((categories ?? []).map(c => [c.id, c.name]))

  const withRecipes = useMemo(() => {
    return (allItems ?? []).filter(i => !i.is_deleted && i.ai_recipe)
  }, [allItems])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return withRecipes
    return withRecipes.filter(i =>
      i.name.toLowerCase().includes(q) ||
      (i.regional_name ?? '').toLowerCase().includes(q) ||
      (catMap[i.category_id ?? ''] ?? '').toLowerCase().includes(q)
    )
  }, [withRecipes, search, catMap])

  if (itemsLoading || catsLoading) return <PageSpinner />

  return (
    <div className="p-6 space-y-5">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">Recipes</h1>
          <p className="text-sm text-stone-500 mt-0.5">
            {withRecipes.length} product{withRecipes.length === 1 ? '' : 's'} with a recipe on file
          </p>
        </div>
      </div>

      <div className="relative max-w-72">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
        <input
          type="text"
          placeholder="Search recipes…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-8 pr-3 py-1.5 text-sm border border-stone-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-400 bg-white"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="card p-10 text-center">
          <ChefHat size={28} className="mx-auto text-stone-300 mb-2" />
          <p className="text-sm text-stone-500">
            {withRecipes.length === 0
              ? "No recipes yet — open a product and use RezzAI to extract one from your notes."
              : 'No recipes match your search.'}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">Product</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">Category</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">Serves</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">Total Time</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {filtered.map(item => {
                const recipe = item.ai_recipe!
                return (
                  <tr key={item.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-stone-900">{item.name}</p>
                      {item.regional_name && <p className="text-xs text-stone-400">{item.regional_name}</p>}
                    </td>
                    <td className="px-4 py-3 text-stone-600">{catMap[item.category_id ?? ''] ?? '—'}</td>
                    <td className="px-4 py-3 text-stone-600">
                      {recipe.serves ? (
                        <span className="inline-flex items-center gap-1"><Users size={12} className="text-stone-400" />{recipe.serves}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-stone-600">
                      {recipe.total_time_minutes ? (
                        <span className="inline-flex items-center gap-1"><Clock size={12} className="text-stone-400" />{recipe.total_time_minutes} min</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/products/items/${item.id}?tab=recipe`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700"
                      >
                        View Recipe
                        <ArrowRight size={12} />
                      </Link>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
